import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/navData';

const FORM_URL = 'https://readdy.ai/api/form/db932ubk13ra8mig90qg';

const sections = [
  {
    icon: 'ri-focus-3-line',
    title: '网站定位',
    content:
      '蓝导航是一款面向大众互联网用户的综合导航平台，覆盖新闻、视频、购物、社交等多个热门网络应用领域，重点解决用户「快速找到目标网站入口」的需求，提升访问效率。',
  },
  {
    icon: 'ri-history-line',
    title: '开发背景',
    content:
      '随着网站数量不断增长，用户常常需要记住大量网址或反复搜索。蓝导航希望通过清晰的分类与精选推荐，把常用网站集中在一个页面，让上网入口变得更简单、更直观。',
  },
  {
    icon: 'ri-service-line',
    title: '服务范围',
    content:
      '平台提供分类导航、站内搜索、主题专题与收藏等基础功能，帮助用户按领域浏览网站入口，并支持一键跳转到目标网站的外链。',
  },
];

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function About() {
  useSeo({
    title: '关于蓝导航 - 综合导航平台介绍',
    description:
      '了解蓝导航的网站定位、开发背景与服务范围，并通过反馈表单向我们提交建议与意见。',
    keywords: '蓝导航,关于我们,综合导航,网站导航,反馈建议',
  });

  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFeedback('感谢你的反馈，我们已收到你的建议。');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFeedback('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const code = parsed?.code;
      const isSpam =
        typeof serverMsg === 'string' &&
        serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFeedback('感谢你的反馈，我们已收到你的建议。');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(
          typeof serverMsg === 'string' && serverMsg.length > 0
            ? serverMsg
            : '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="cursor-pointer hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">关于我们</span>
      </nav>

      <header className="mt-4 overflow-hidden rounded-xl border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-primary-600">
          <i className="ri-compass-3-line"></i>
          关于蓝导航
        </span>
        <h1 className="mt-4 font-heading text-3xl font-extrabold text-foreground-950 md:text-4xl">
          便捷的综合导航平台
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          蓝导航为用户提供丰富实用的网页导航入口，涵盖新闻、视频、购物、社交等多领域，助您快速访问所需网站。我们希望用清晰的分类和持续更新的精选内容，让每个人都能更轻松地探索互联网。
        </p>
      </header>

      <section className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
        {sections.map((section) => (
          <article
            key={section.title}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
              <i className={section.icon}></i>
            </span>
            <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">
              {section.title}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              {section.content}
            </p>
          </article>
        ))}
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              反馈与建议
            </h2>
            <p className="mt-1 text-sm text-foreground-500">
              欢迎告诉我们你想收录的网站，或对蓝导航的任何建议。
            </p>

            <form
              id="about-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">
                    昵称
                  </span>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="请输入你的昵称"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground-700">
                    邮箱
                  </span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="用于接收我们的回复"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground-700">
                  反馈类型
                </span>
                <select
                  name="topic"
                  defaultValue="网站收录"
                  className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                >
                  <option value="网站收录">网站收录</option>
                  <option value="功能建议">功能建议</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="商务合作">商务合作</option>
                </select>
              </label>

              <label className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-foreground-700">
                  反馈内容
                </span>
                <textarea
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="请输入不超过 500 字的反馈内容"
                  className="resize-none rounded-md border border-background-300 bg-background-50 p-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
              </label>

              <input
                className="feedback-company-field"
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
                >
                  <i
                    className={
                      status === 'submitting'
                        ? 'ri-loader-4-line animate-spin'
                        : 'ri-send-plane-line'
                    }
                  ></i>
                  {status === 'submitting' ? '提交中…' : '提交反馈'}
                </button>
                <span className="text-xs text-foreground-400">
                  演示表单 · 提交后不会产生真实业务处理
                </span>
              </div>

              {status === 'success' && (
                <p className="flex items-center gap-1.5 rounded-md bg-secondary-50 px-3.5 py-2.5 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line"></i>
                  {feedback}
                </p>
              )}
              {status === 'error' && (
                <p className="flex items-center gap-1.5 rounded-md bg-accent-50 px-3.5 py-2.5 text-sm text-accent-800">
                  <i className="ri-error-warning-line"></i>
                  {feedback}
                </p>
              )}
            </form>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              联系方式
            </h2>
            <ul className="mt-4 flex flex-col gap-3 text-sm">
              <li className="flex items-center gap-2.5 text-foreground-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                hello@lannav.example
              </li>
              <li className="flex items-center gap-2.5 text-foreground-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-time-line"></i>
                </span>
                工作日 09:00 - 18:00
              </li>
              <li className="flex items-center gap-2.5 text-foreground-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-600">
                  <i className="ri-map-pin-line"></i>
                </span>
                线上服务 · 服务全国用户
              </li>
            </ul>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              使用说明与免责声明
            </h2>
            <ul className="mt-3 flex flex-col gap-2.5 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-500"></i>
                本站导航入口均为外链跳转，点击后将在新标签页打开目标网站。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-500"></i>
                搜索、收藏与反馈功能均为前端演示，数据仅保存在本地或作示例展示。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-500"></i>
                第三方网站的内容与服务质量由其自身提供，蓝导航仅作收录与整理，不对其内容负责。
              </li>
            </ul>
          </div>

          <div className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">
              快速浏览分类
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="cursor-pointer rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-600 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  {category.label}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}